import { useQueryClient } from '@tanstack/react-query';
import {
  customerAdjustmentSchema,
  CUSTOMER_MOVEMENT_LABELS,
  PAYMENT_METHOD_LABELS,
  voidCustomerMovementSchema,
  type CustomerDTO,
  type CustomerMovementDTO,
  type CustomerMovementType,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Input,
  Kpi,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  PageHeader,
  Skeleton,
  Textarea,
  useToast,
  type Tone,
} from '@bifeai/ui';
import { Archive, ArrowLeft, HandCoins, MessageCircle, MoreHorizontal, Pencil, Printer, Receipt, Scale, ShoppingBag } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { CollectDialog } from '../../components/customers/CollectDialog';
import { CustomerFormDialog } from '../../components/customers/CustomerFormDialog';
import { SaleDetailDialog } from '../../components/SaleDetailDialog';
import { api, errorMessage } from '../../lib/api';
import { useCustomer, usePosSettings, whatsappLink } from '../../lib/customers';
import { formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';
import { printPaymentReceipt, printStatement } from '../../lib/ticket-print';
import { pesosToCents } from '../../lib/units';

const TYPE_TONE: Record<CustomerMovementType, Tone> = {
  SALE: 'danger',
  SALE_VOID: 'neutral',
  PAYMENT: 'success',
  PAYMENT_VOID: 'warning',
  ADJUSTMENT: 'info',
};

export function CustomerPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const { me, can } = useSession();
  const manage = can('customers.manage');
  const q = useCustomer(id ?? null);
  const settings = usePosSettings();
  const [dialog, setDialog] = useState<'edit' | 'collect' | 'adjust' | null>(null);
  const [voiding, setVoiding] = useState<CustomerMovementDTO | null>(null);
  const [sale, setSale] = useState<string | null>(null);
  const c = q.data;
  const businessName = settings.data?.ticket.businessName || me.tenant.name;

  const refresh = (updated?: CustomerDTO) => {
    if (updated) qc.setQueryData(['customers', 'detail', updated.id], updated);
    return Promise.all(['customers', 'dashboard', 'cash', 'notifications'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
  };

  async function setArchived(archived: boolean) {
    if (!c) return;
    try {
      const updated = await api.post<CustomerDTO>(`/customers/${c.id}/archive`, { archived });
      toast.success(archived ? 'Cliente archivado' : 'Cliente recuperado');
      await refresh(updated);
    } catch (err) {
      toast.error('No se pudo', errorMessage(err));
    }
  }

  if (q.isError) {
    return (
      <Card>
        <EmptyState icon={<ShoppingBag />} title="No encontramos el cliente" action={<Link to="/clientes">Volver a clientes</Link>} />
      </Card>
    );
  }
  if (!c) return <Skeleton style={{ height: 300 }} />;
  const wa = whatsappLink(c, businessName);

  return (
    <>
      <Link to="/clientes" className="bf-back">
        <ArrowLeft size={16} /> Clientes
      </Link>
      <PageHeader
        title={
          <>
            {c.name} {c.archived && <Badge>Archivado</Badge>} {c.overdue && <Badge tone="warning">Deuda atrasada</Badge>}
          </>
        }
        subtitle={[c.phone, c.docNumber && `DNI/CUIT ${c.docNumber}`, c.address].filter(Boolean).join(' · ') || 'Sin datos de contacto'}
        actions={
          <>
            {c.balanceCents > 0 && (
              <Button variant="primary" icon={<HandCoins />} onClick={() => setDialog('collect')}>
                Cobrar
              </Button>
            )}
            {wa && (
              <a className="bf-btn" href={wa} target="_blank" rel="noreferrer">
                <MessageCircle /> WhatsApp
              </a>
            )}
            {settings.data && (
              <Button icon={<Printer />} onClick={() => printStatement(c, businessName, settings.data!.ticket)}>
                Imprimir cuenta
              </Button>
            )}
            {manage && (
              <Menu>
                <MenuTrigger asChild>
                  <Button iconOnly icon={<MoreHorizontal />}>
                    Más opciones
                  </Button>
                </MenuTrigger>
                <MenuContent align="end">
                  <MenuItem onSelect={() => setDialog('edit')}>
                    <Pencil /> Editar datos y límite
                  </MenuItem>
                  <MenuItem onSelect={() => setDialog('adjust')}>
                    <Scale /> Ajustar saldo
                  </MenuItem>
                  <MenuItem onSelect={() => void setArchived(!c.archived)}>
                    <Archive /> {c.archived ? 'Recuperar cliente' : 'Archivar cliente'}
                  </MenuItem>
                </MenuContent>
              </Menu>
            )}
          </>
        }
      />

      <div className="bf-kpi-grid">
        <Kpi
          label="Debe"
          tone="red"
          icon={<HandCoins />}
          value={c.balanceCents < 0 ? `A favor ${formatMoney(-c.balanceCents)}` : formatMoney(c.balanceCents)}
          delta={c.lastMovementAt ? `Último movimiento: ${formatDate(c.lastMovementAt)}` : 'Sin movimientos'}
          deltaMuted
        />
        <Kpi
          label="Límite de fiado"
          tone="amber"
          value={c.creditLimitCents > 0 ? formatMoney(c.creditLimitCents) : 'Sin fiado'}
          delta={
            c.creditLimitCents > 0
              ? `Puede fiar ${formatMoney(c.availableCents)} más`
              : manage
                ? 'Ponele un límite desde "Más opciones"'
                : 'Lo habilita el dueño o el encargado'
          }
          deltaMuted
        />
        <Kpi
          label="Compras"
          tone="green"
          icon={<Receipt />}
          value={formatMoney(c.stats.purchasesCents)}
          delta={`${c.stats.purchasesCount} ${c.stats.purchasesCount === 1 ? 'compra' : 'compras'}${c.stats.lastPurchaseAt ? ` · última el ${formatDate(c.stats.lastPurchaseAt)}` : ''}`}
          deltaMuted
        />
      </div>

      {c.notes && <Callout>{c.notes}</Callout>}

      <Card>
        <CardHeader title="Cuenta corriente" subtitle="Compras fiadas, pagos y ajustes. Lo anulado queda a la vista, tachado." />
        <CardBody flush>
          {c.movements.length === 0 ? (
            <EmptyState icon={<Receipt />} title="Sin movimientos" text="Cuando compre con cuenta corriente o pague, aparece acá." />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Movimiento</th>
                    <th style={{ textAlign: 'right' }}>Importe</th>
                    <th style={{ textAlign: 'right' }}>Saldo</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {c.movements.map((m) => (
                    <tr key={m.id} style={{ opacity: m.reversed ? 0.55 : 1 }}>
                      <td className="bf-cell-sub">{formatDateTime(m.createdAt)}</td>
                      <td>
                        <Badge tone={TYPE_TONE[m.type]}>{CUSTOMER_MOVEMENT_LABELS[m.type]}</Badge>{' '}
                        {m.saleId && m.ticket && (
                          <button type="button" className="bf-link-btn" onClick={() => setSale(m.saleId)}>
                            Ticket {m.ticket}
                          </button>
                        )}
                        {m.reversed && <Badge>Anulado</Badge>}
                        <div className="bf-cell-sub">
                          {[m.method && PAYMENT_METHOD_LABELS[m.method], m.reference, m.notes, m.branchName, m.createdByName]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </td>
                      <td
                        className={`bf-num ${m.amountCents > 0 ? 'bf-text-danger' : 'bf-text-success'}`}
                        style={{
                          textAlign: 'right',
                          fontWeight: 600,
                          textDecoration: m.reversed ? 'line-through' : undefined,
                        }}
                      >
                        {m.amountCents > 0 ? '+' : '−'}
                        {formatMoney(Math.abs(m.amountCents))}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {formatMoney(m.balanceAfterCents)}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {m.type === 'PAYMENT' && !m.reversed && settings.data && (
                          <Button size="sm" variant="ghost" onClick={() => printPaymentReceipt(c, m, businessName, settings.data!.ticket)}>
                            Recibo
                          </Button>
                        )}
                        {m.canVoid && (
                          <Button size="sm" variant="ghost" onClick={() => setVoiding(m)}>
                            Anular
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {dialog === 'edit' && (
        <CustomerFormDialog
          customer={c}
          onClose={() => setDialog(null)}
          onSaved={async (u) => {
            setDialog(null);
            toast.success('Cliente actualizado');
            await refresh(u);
          }}
        />
      )}
      {dialog === 'collect' && (
        <CollectDialog
          customer={c}
          onClose={() => setDialog(null)}
          onDone={async (u) => {
            setDialog(null);
            toast.success('Pago registrado', u.balanceCents > 0 ? `Queda debiendo ${formatMoney(u.balanceCents)}.` : 'La cuenta quedó al día.');
            await refresh(u);
          }}
        />
      )}
      {dialog === 'adjust' && (
        <AdjustDialog
          customer={c}
          onClose={() => setDialog(null)}
          onDone={async (u) => {
            setDialog(null);
            toast.success('Saldo ajustado');
            await refresh(u);
          }}
        />
      )}
      {voiding && (
        <VoidMovementDialog
          movement={voiding}
          onClose={() => setVoiding(null)}
          onDone={async (u) => {
            setVoiding(null);
            toast.success('Movimiento anulado');
            await refresh(u);
          }}
        />
      )}
      {sale && <SaleDetailDialog saleId={sale} onClose={() => setSale(null)} />}
    </>
  );
}

function AdjustDialog({ customer, onClose, onDone }: { customer: CustomerDTO; onClose: () => void; onDone: (c: CustomerDTO) => Promise<void> }) {
  const [sign, setSign] = useState<1 | -1>(1);
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = customerAdjustmentSchema.safeParse({
      amountCents: sign * (pesosToCents(amount) ?? 0),
      notes,
    });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Revisá los datos.');
    setBusy(true);
    setError(null);
    try {
      await onDone(await api.post<CustomerDTO>(`/customers/${customer.id}/adjustments`, parsed.data));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title="Ajustar saldo"
      description="Para cargar una deuda que traía de la libreta, o corregir un error. Queda registrado quién lo hizo y por qué."
    >
      <form className="bf-form" onSubmit={submit} noValidate>
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="bf-segmented bf-segmented--text" role="group" aria-label="Tipo de ajuste">
          <button type="button" aria-pressed={sign === 1} onClick={() => setSign(1)}>
            Suma deuda
          </button>
          <button type="button" aria-pressed={sign === -1} onClick={() => setSign(-1)}>
            Descuenta deuda
          </button>
        </div>
        <Field label="Importe">
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus placeholder="$ 0" />
        </Field>
        <Field label="Motivo">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ej.: deuda anterior anotada en la libreta" />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            Guardar ajuste
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function VoidMovementDialog({
  movement,
  onClose,
  onDone,
}: {
  movement: CustomerMovementDTO;
  onClose: () => void;
  onDone: (c: CustomerDTO) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    const parsed = voidCustomerMovementSchema.safeParse({ reason });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Contá el motivo.');
    setBusy(true);
    setError(null);
    try {
      await onDone(await api.post<CustomerDTO>(`/customers/movements/${movement.id}/void`, parsed.data));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={`Anular ${CUSTOMER_MOVEMENT_LABELS[movement.type].toLowerCase()}`}
      description={`${formatMoney(Math.abs(movement.amountCents))} del ${formatDateTime(movement.createdAt)}. El movimiento queda a la vista, tachado, y se agrega la anulación.`}
    >
      <div className="bf-form">
        {movement.method === 'CASH' && movement.type === 'PAYMENT' && (
          <Callout tone="warning">Era en efectivo: se descuenta del efectivo esperado de la caja.</Callout>
        )}
        <Field label="Motivo" error={error ?? undefined}>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus placeholder="Ej.: se cargó dos veces" />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={busy}>
            Volver
          </Button>
          <Button variant="danger" loading={busy} onClick={() => void submit()}>
            Anular
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
